import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Modal, ModalProps } from '@vimily/bonjoro-ui-components-v2/Modal';
import DayPicker from 'react-day-picker';
import { DateTime } from 'luxon';
import {
  TimeFormat,
  getDateFromDatetime,
  createHourTz,
  getHourIn12FormatFromDatetime,
  getHourIn24Format,
  getTimeFormatFromDatetime,
  isFuture,
} from '~/utils/date';
import { useTimezones } from '~/App/Api/Timezone';
import { Timezone } from '~/types/models';
import useCurrentUser from '~/react-hooks/use-current-user';
import DateFullDescription from '~/App/Modules/Common/Components/Greet/Scheduled/DateFullDescription';
import {
  DropdownSelector,
  SelectorType,
} from '@vimily/bonjoro-ui-components-v2/DropdownSelector';
import { ToggleSelector } from '@vimily/bonjoro-ui-components-v2/ToggleSelector';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import cx from 'classnames';
import { DropdownAlignment } from '@vimily/bonjoro-ui-components-v2/Dropdown';
import { AnimatedDiv } from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import defaultDayPickerClassNames from 'react-day-picker/src/classNames';
import { ClassNames } from 'react-day-picker/types/ClassNames';

export interface SchedulerModalProps extends ModalProps {
  onConfirm: (dateTime: DateTime, timezone?: Timezone, time?: string) => void;
  onCancel?: () => void;
  scheduleDateTime?: DateTime;
  onDateChange?: (dateTime: DateTime) => void;
  onTimeZoneChange?: (timezone: Timezone) => void;
  onHourChange?: (time: number) => void;
  onTimeFormatChange?: (format: TimeFormat) => void;
}

export const SchedulerModal = ({
  onConfirm,
  onCancel,
  scheduleDateTime: defaultScheduleDateTime,
  onDateChange,
  onTimeZoneChange,
  onHourChange,
  ...modalProps
}: SchedulerModalProps) => {
  const user = useCurrentUser();
  const [scheduledDatetime, setScheduledDatetime] = useState(
    defaultScheduleDateTime
  );

  const [hour, setHour] = useState<number>(
    getHourIn12FormatFromDatetime(scheduledDatetime)
  );
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(
    getTimeFormatFromDatetime(scheduledDatetime)
  );
  const [selectedDate, setSelectedDate] = useState<Date>(
    getDateFromDatetime(scheduledDatetime)
  );
  const [errorMessage, setErrorMessage] = useState<string>();

  const defaultTimezone: Timezone = {
    value: user.timezone || 'UTC',
    label: user.timezone || '(UTC) UTC',
  };
  const [timezoneSelection, setTimezoneSelection] =
    useState<Timezone>(defaultTimezone);

  useEffect(() => {
    const sendDateTime = createHourTz(
      getDateFromDatetime(defaultScheduleDateTime),
      defaultTimezone,
      defaultScheduleDateTime?.hour ?? 0
    );

    setHour(getHourIn12FormatFromDatetime(sendDateTime));
    setTimeFormat(getTimeFormatFromDatetime(sendDateTime));
    setSelectedDate(getDateFromDatetime(sendDateTime));
  }, [defaultScheduleDateTime]);

  const onConfirmHandler = useCallback(() => {
    if (!scheduledDatetime || errorMessage !== undefined) {
      setErrorMessage('Please select a date and time to schedule a greet.');
      return;
    }
    if (!isFuture(scheduledDatetime)) {
      setErrorMessage('Please select date and time which is in the future.');
      return;
    }
    setErrorMessage(undefined);
    onConfirm(scheduledDatetime, timezoneSelection, `${hour}${timeFormat}`);
  }, [scheduledDatetime, hour, timezoneSelection, timeFormat, errorMessage]);

  const handleHourChange = (event: ChangeEvent<HTMLInputElement>) => {
    const hour = event.target.value;
    const selectedHour = parseInt(hour);
    if (isNaN(selectedHour)) {
      return;
    }
    if (selectedHour < 1 || selectedHour > 12) {
      setErrorMessage('Please select hours between 1-12');
      return;
    }
    setHour(selectedHour);
    onHourChange?.(selectedHour);
  };

  const handleDateChange = (day: Date) => {
    setSelectedDate(day);
  };

  const onCancelHandler = () => {
    modalProps?.onModalClose?.();
    onCancel?.();
  };

  useEffect(() => {
    const sendDateTime = createHourTz(
      selectedDate,
      timezoneSelection,
      getHourIn24Format(hour, timeFormat)
    );

    if (!isFuture(sendDateTime)) {
      setErrorMessage('Please select date and time which is in the future');
      return;
    }
    setScheduledDatetime(sendDateTime);
    onDateChange?.(sendDateTime);

    setErrorMessage(undefined);
  }, [selectedDate, hour, timezoneSelection, timeFormat]);

  const { data: timezones = [] } = useTimezones();

  const items = useMemo(() => {
    if (!timezones) return {};

    return timezones.reduce((items, timezone) => {
      return {
        ...items,
        [timezone.value]: timezone.label,
      };
    }, {}) as Record<string, string>;
  }, [timezones]);

  const onTimezoneSelect = (selected: string[]) => {
    if (timezones && timezones.length === 0) return;
    const timezone = timezones.filter((timezone) =>
      selected.includes(`${timezone.value}`)
    )[0];
    setTimezoneSelection(timezone);
    onTimeZoneChange?.(timezone);
  };

  const onTimeFormatChange = (type: string) => {
    setTimeFormat(type as TimeFormat);
    onTimeFormatChange?.(type);
  };

  const hasError = errorMessage != undefined;

  return (
    <Modal
      {...modalProps}
      className='sm:!w-[630px] !rounded-lg !inset-px !p-0'
      overlayClassName='!z-[9999999999]'
      closeButtonClassName='!top-7.5 !right-7.5 text-[30px]'
    >
      <div className='flex flex-col gap-y-5 w-full p-5'>
        <span className='v2-text-neutral-900 text-[21px] font-bold'>
          Schedule to send on
        </span>
        <div className='flex flex-row gap-x-5'>
          <DayPicker
            selectedDays={selectedDate}
            onDayClick={handleDateChange}
            fromMonth={new Date()}
            initialMonth={selectedDate}
            disabledDays={{ before: new Date() }}
            className='flex-1 Selectable v2-border-neutral-300 v2-border-solid border rounded h-fit'
            classNames={{
              ...(defaultDayPickerClassNames as ClassNames),
              disabled:
                'DayPicker-Day--disabled !cursor-not-allowed hover:!v2-bg-white',
              outside:
                'DayPicker-Day--outside !v2-bg-white !v2-text-white hover:!v2-bg-white !cursor-default',
              today: 'DayPicker-Day--today !v2-text-blue-500',
              selected:
                'relative v2-bg-yellow-50 v2-text-neutral-900 font-bold',
              day: 'table-cell align-middle text-center text-xs hover:v2-bg-yellow-50 h-[30px] w-[30px] !rounded !p-3 cursor-pointer',
              month: 'DayPicker-Month !mx-2',
            }}
          />
          <div className='flex flex-col gap-y-4 justify-center w-[290px]'>
            <DateFullDescription
              className={
                'rounded v2-bg-neutral-50 font-bold my-2 py-2 px-3 text-md text-center'
              }
              datetime={
                scheduledDatetime ||
                DateTime.local().setZone(timezoneSelection.value)
              }
            />
            <DropdownSelector
              dropdownProps={{
                id: 'timezone-selector',
                containerClassName: '!z-10',
                containerAlignment: DropdownAlignment.Center,
                width: 290,
              }}
              bodyContainerClassName='!w-full v2-border-neutral-300 v2-border-[1px]'
              bodyLabelClassName='text-base'
              type={SelectorType.Select}
              items={items}
              selected={
                timezoneSelection?.value
                  ? [`${timezoneSelection?.value}`]
                  : undefined
              }
              onSelect={onTimezoneSelect}
            />
            <div className='flex flex-row gap-x-2'>
              <Input
                className='font-bold text-[21px] text-right pr-3'
                value={hour}
                required
                max={12}
                type='number'
                min={1}
                onChange={handleHourChange}
              />
              <ToggleSelector
                items={{
                  [TimeFormat.AM]: 'AM',
                  [TimeFormat.PM]: 'PM',
                }}
                selected={timeFormat}
                onSelect={onTimeFormatChange}
                className='!rounded'
                buttonClassName={cx([
                  'font-bold v2-border-x-neutral-300 hover:!v2-bg-yellow-50',
                  'text-[21px] !rounded v2-bg-white border-none',
                ])}
                activeButtonClassName='!v2-bg-yellow-50'
              />
            </div>
            {hasError && (
              <AnimatedDiv
                show={hasError}
                className='px-4 py-2 rounded text-center v2-bg-red-100 v2-text-red-700'
                delay={100}
              >
                <span className='block sm:inline'>{errorMessage}</span>
              </AnimatedDiv>
            )}
          </div>
        </div>

        <div className='flex flex-row gap-x-3 justify-end'>
          <Button
            className='!text-base'
            onClick={onCancelHandler}
            variant={ButtonVariant.Secondary}
          >
            Cancel
          </Button>
          <Button
            className='!text-base'
            disabled={hasError}
            variant={ButtonVariant.Primary}
            onClick={onConfirmHandler}
          >
            Confirm and schedule
          </Button>
        </div>
      </div>
    </Modal>
  );
};
